import { useMemo } from 'react';
import { atom, reatomAsync, withAbort, withErrorAtom } from '@reatom/framework';
import { useCtx } from '@reatom/npm-react';
import { createObservableFromAtom } from '@/js/libs/observable/index.ts';
import { getComponentDebugName } from '@/js/libs/reatom-devtools/index.ts';
import { parseError } from './parseError.js';
import { DEFAULT_QUERY_PARAMS } from './useRequest.js';
import { useRequestContextValue } from './UseRequestContext.js';
export function createLazyFetch(apiMethod, config, _ctx) {
    const { ignoreErrors, queryDefaults, ignoreHeaders, name = `${apiMethod.name}Lazy` } = config;
    const cancelPreviousRequests = _ctx.get(config.cancelPreviousRequests);
    const fetchAction = reatomAsync(async (ctx, params, silent = false) => {
        try {
            const response = await ctx.schedule(() => apiMethod({
                params,
                signal: ctx.controller.signal,
                ignoreErrors: ctx.get(ignoreErrors),
                ignoreHeaders,
                queryDefaults: ctx.get(queryDefaults),
                onUploadProgress: ctx.get(config.onUploadProgress),
            }));
            ctx.get(config.onSuccess)?.(response.data, params, response.status);
            return response;
        }
        catch (e) {
            // `silent` is captured per call, so concurrent requests don't clobber each other's intent
            const error = parseError(e, name);
            // safety call `onFail` before render
            if (error && !silent)
                ctx.schedule(() => ctx.get(config.onFail)?.(error));
            throw e;
        }
    }, name).pipe(withAbort({ strategy: cancelPreviousRequests ? 'last-in-win' : 'none' }), withErrorAtom((_ctx, e) => parseError(e, name)));
    fetchAction.pendingAtom.__reatom.name = fetchAction.pendingAtom.__reatom.name?.replace('pendingAtom', '_pendingAtom');
    const isLoadingAtom = atom((ctx) => ctx.spy(fetchAction.pendingAtom) > 0, `${name}.isLoadingAtom`);
    return {
        error$: createObservableFromAtom(fetchAction.errorAtom),
        pending$: createObservableFromAtom(isLoadingAtom),
        load: (params, options) => fetchAction(_ctx, params, options?.silent ?? false)
            .then((response) => response.data)
            .catch((err) => {
            if (cancelPreviousRequests) {
                // message described inside withAbort
                if (err && typeof err === 'object' && 'message' in err && typeof err.message === 'string' && err.message.startsWith('abort ')) {
                    // convert back to default abort error
                    const abortError = new Error();
                    abortError.name = 'AbortError';
                    throw abortError;
                }
            }
            // Rethrow other errors
            throw err;
        }),
        abort: () => fetchAction.abort(_ctx),
    };
}
export function useLazyRequest(props) {
    const { onFail: onFailFromContext } = useRequestContextValue();
    const { apiMethod, cancelPreviousRequests = true, onFail: onFailFromProps, onSuccess, onUploadProgress, ignoreErrors, ignoreHeaders, queryDefaults = DEFAULT_QUERY_PARAMS, } = props;
    const onFail = onFailFromProps ?? onFailFromContext;
    const ctx = useCtx();
    const name = getComponentDebugName(`${apiMethod.name}Lazy`);
    const config = useMemo(() => ({
        cancelPreviousRequests: atom(cancelPreviousRequests, `${name}._cancelPreviousRequests`),
        onFail: atom(undefined, `${name}._onFail`),
        onSuccess: atom(undefined, `${name}._onSuccess`),
        onUploadProgress: atom(undefined, `${name}._onUploadProgress`),
        ignoreErrors: atom(ignoreErrors ?? null),
        queryDefaults: atom(queryDefaults),
        ignoreHeaders,
        name,
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    if (ctx.get(config.onFail) !== onFail) {
        config.onFail(ctx, () => onFail);
    }
    if (ctx.get(config.onSuccess) !== onSuccess) {
        config.onSuccess(ctx, () => onSuccess);
    }
    if (ctx.get(config.onUploadProgress) !== onUploadProgress) {
        config.onUploadProgress(ctx, () => onUploadProgress);
    }
    if (ctx.get(config.cancelPreviousRequests) !== cancelPreviousRequests) {
        config.cancelPreviousRequests(ctx, () => cancelPreviousRequests);
    }
    return useMemo(() => createLazyFetch(apiMethod, config, ctx), [apiMethod, config, ctx]);
}
